import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  inject,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  nextTick,
} from "vue";
import router from "@/router";
import MainLayout from "./layouts/MainLayout.vue";
import AgreementsModal from "@/components/modals/AgreementsModal.vue";
import { useUserStore } from "@/store/user";
import { useCoachStore } from "@/store/coach";
import { useSupportChat } from "@/composables/useSupportChat";
import dayjs from "dayjs";
import FailedStatusModal from "@/components/modals/FailedStatusModal.vue";
import { useManageSubscription } from "@/composables/useManageSubscription";
import IChat from "@/types/IChat";
import coachApi from "@/services/api/coachApi";
import userApi from "@/services/api/userApi";
import { useChatStore } from "@/store/chat";
import { useAuthStore } from "@/store/auth";


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props, { expose }) {
  expose();

const socket: any = inject("socket");
const socketChat: any = inject("socketChat");
const userStore = useUserStore();
const coachStore = useCoachStore();
const chatStore = useChatStore();
const authStore = useAuthStore();
const { isCoach, isUser, isAnyAuthorized, isSignedAtTermsOfUse, getChats } =
  useSupportChat(socketChat);
const isOpenFailedStatusModal = ref<boolean>(false);
const isOpenTermsOfUseModal = ref(false);

const layout = computed(() => {
  return router.currentRoute.value.meta.layout || MainLayout;
});

const isActualAgreements = computed(() => {
  return (
    userStore.data.termsAgreement?.isActual &&
    userStore.data.policyAgreement?.isActual
  );
});

const isAccessibleRouterData = computed(() => {
  return (
    router.currentRoute.value.name &&
    ![
      "/terms-of-use",
      "/privacy-policy",
      "/support/login",
      "/login",
      "/registration",
      "/",
      "/change-email",
      "/upgrade-options",
    ].includes(router.currentRoute.value.path)
  );
});

const { isActiveMainSubs, isPastDueMainSubs } = useManageSubscription();

const onNewMessageListener = (res: any): void => {
  if (!res.error) {
    chatStore.updateDataById({ data: res.data, type: "inbox" });
  }
};

const checkTermsAgreement = (): void => {
  if (
    isUser.value &&
    !isActualAgreements.value &&
    isAccessibleRouterData.value
  ) {
    isOpenTermsOfUseModal.value = true;
  }
};
let socketChatConnectInterval: number | undefined;

onMounted(() => {
  checkTermsAgreement();

  socket.on("connect", function () {
    if (isUser.value) {
      socket.emit("sub-on-update", {}, (res: any) => {
        console.log("sub-on-update :>> ", res);
        if (res.error) {
          console.log(res.error);
        }
      });
    }
  });

  socket.on("subscription-update", function (data) {
    if (data.data.subscription) {
      userStore.data.subscription = data.data.subscription;
      userStore.settings.subscription.failedStatus =
        data.data.subscription.status === "past_due"
          ? dayjs().format("YYYY-MM-DD")
          : "";
      if (
        router.currentRoute.value.name === "StubPage" &&
        isActiveMainSubs.value
      ) {
        router.push({ name: "Dashboard" });
      }
    }
    if (data.data.extraSubscription) {
      if (userStore.data.extraSubscriptions?.length) {
        const index = userStore.data.extraSubscriptions.findIndex(
          (item) => item.productId === data.data.extraSubscription.productId
        );
        if (index < 0) {
          userStore.data.extraSubscriptions.push(data.data.extraSubscription);
        } else {
          userStore.data.extraSubscriptions[index] =
            data.data.extraSubscription;
        }
      } else {
        userStore.data.extraSubscriptions = [];
        userStore.data.extraSubscriptions.push(data.data.extraSubscription);
      }
    }
  });

  socket.on("migration-update", function (data) {
    if (data.data.migratedToV2At && !userStore.data.migratedToV2At) {
      userStore.data.migratedToV2At = data.data.migratedToV2At;
      router.push({ name: "Dashboard" });
    }
  });

  socket.on("on-unauthorized", function () {
    socket.disconnect();
  });

  socketChat.on("connect", function () {
    if (socketChat.connected && !isCoach.value && isSignedAtTermsOfUse.value) {
      getChats().then((res) => {
        chatStore.setData(res as IChat[]);
      });
    }
  });

  if (!isCoach.value && isSignedAtTermsOfUse.value) {
    socketChat.on("new-message", onNewMessageListener);
  }

  socketChat.on("disconnect", function () {
    console.log("chat-disconnect");
  });

  socketChat.on("on-unauthorized", function () {
    socketChat.disconnect();
    updateSocketChatToken();
  });

  const updateSocketChatToken = () => {
    if (
      (userStore.accessToken || coachStore.coachAccessToken) &&
      !userStore.settings.isRefreshingToken
    ) {
      userStore.settings.isRefreshingToken = true;
      (isCoach.value ? coachStore : authStore).refreshToken().finally(() => {
        userStore.settings.isRefreshingToken = false;
        reconnectSocketChat();
      });
    }
  };

  const reconnectSocketChat = () => {
    if (!userStore.settings.isRefreshingToken) {
      if (
        !socketChat.connected &&
        isAnyAuthorized.value &&
        isSignedAtTermsOfUse.value
      ) {
        socketChat.io.opts.transportOptions.polling.extraHeaders.Authorization = `Bearer ${
          userStore.accessToken || coachStore.coachAccessToken
        }`;
        socketChat.connect();
      } else if (socketChat.connected && !isAnyAuthorized.value) {
        socketChat.disconnect();
      }
    }
  };

  const notificationsID = setInterval(() => {
    if (!socket.connected && isUser.value) {
      socket.disconnect();
      socket.io.opts.transportOptions.polling.extraHeaders.Authorization = `Bearer ${userStore.accessToken}`;
      socket.connect();
    } else if (socket.connected && !userStore.accessToken) {
      socket.disconnect();
    } else if (isCoach.value) {
      clearInterval(notificationsID);
    }
  }, 1500);
});

onBeforeUnmount(() => {
  socketChat.off("new-message", onNewMessageListener);
  clearInterval(socketChatConnectInterval);
});

watch(
  () => userStore.settings.subscription.failedStatus,
  () => {
    if (
      isPastDueMainSubs.value &&
      userStore.settings.subscription.failedStatus
    ) {
      isOpenFailedStatusModal.value = true;
    }
  },
  { immediate: true }
);

watch(
  () => router.currentRoute.value.path,
  () => {
    checkTermsAgreement();
  }
);

watch(
  () => isAnyAuthorized.value,
  (newV: boolean) => {
    if (newV && !socketChat.connected) {
      socketChat.disconnect();
      socketChat.io.opts.transportOptions.polling.extraHeaders.Authorization = `Bearer ${
        userStore.accessToken || coachStore.coachAccessToken
      }`;
      socketChat.connect();
    }
  }
);

const __returned__ = { socket, socketChat, userStore, coachStore, chatStore, authStore, isCoach, isUser, isAnyAuthorized, isSignedAtTermsOfUse, getChats, isOpenFailedStatusModal, isOpenTermsOfUseModal, layout, isActualAgreements, isAccessibleRouterData, isActiveMainSubs, isPastDueMainSubs, onNewMessageListener, checkTermsAgreement, socketChatConnectInterval, computed, inject, onMounted, onBeforeUnmount, ref, watch, nextTick, router, MainLayout, AgreementsModal, useUserStore, useCoachStore, useSupportChat, dayjs, FailedStatusModal, useManageSubscription, IChat, coachApi, userApi, useChatStore, useAuthStore }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})